import React, { useEffect, useState } from 'react';
import { Box, Slider } from '@mui/material';

interface OrderingRangeSliderProps {
    min: number;
    max: number;
    onChange: (range: { min: number; max: number }) => void;
    name: string;
    featureId: string;
    onRangeChange: (featureId: string, rangeFrom: string, rangeTo: string) => void;
    clear?: number;
}

// Two vertical-bar thumbs in distinct colors, one per end of the range.
const sliderSx = {
    color: '#821818',
    display: 'block',
    '& .MuiSlider-thumb': {
        width: 6,
        height: 22,
        borderRadius: 0,
    },
    '& .MuiSlider-thumb[data-index="0"]': {
        backgroundColor: '#757575',
    },
    '& .MuiSlider-thumb[data-index="1"]': {
        backgroundColor: '#821818',
    },
    '& .MuiSlider-track': {
        backgroundColor: '#821818',
    },
};

// Step is derived from the feature's min/max span - a fixed step of 1 would leave only the two endpoints
// reachable for a narrow domain (e.g. 1.5-2).
const OrderingRangeSlider: React.FC<OrderingRangeSliderProps> = ({
    min,
    max,
    onChange,
    featureId,
    onRangeChange,
    clear,
}) => {
    const [range, setRange] = useState<number[]>([min, max]);
    const step = max > min ? (max - min) / 100 : undefined;

    useEffect(() => {
        setRange([min, max]);
    }, [min, max]);

    useEffect(() => {
        if (clear !== undefined && clear > 0) {
            setRange([min, max]);
        }
    }, [clear]);

    const commitRange = (newRange: number[]) => {
        onChange({ min: newRange[0], max: newRange[1] });
        onRangeChange(featureId, newRange[0].toString(), newRange[1].toString());
    };

    useEffect(() => {
        commitRange(range);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box
            sx={{
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                px: 2,
            }}
        >
            <Slider
                value={range}
                min={min}
                max={max}
                step={step}
                onChange={(_event, newValue) => setRange(newValue as number[])}
                onChangeCommitted={(_event, newValue) => commitRange(newValue as number[])}
                valueLabelDisplay="auto"
                disableSwap
                size="small"
                sx={sliderSx}
            />
        </Box>
    );
};

export default OrderingRangeSlider;
